const usdFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/**
 * Money as a person reads it. Accepts the decimal strings the API sends (so
 * precision survives the wire) as well as plain numbers.
 *
 * Null/unparseable renders as "—", never "$0.00": "we have no usage data" and
 * "this was free" are different claims and the caller usually can't tell which
 * one applies. Sub-cent amounts keep 6 decimals — per-call agent costs are
 * routinely $0.0004 and rounding them to $0.00 hides real spend.
 */
export function formatUsd(amount: string | number | null | undefined): string {
  if (amount == null) return "—";
  const dollars = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  if (!Number.isFinite(dollars)) return "—";
  const magnitude = Math.abs(dollars);
  // Sign is applied outside the formatter so tiny negatives that round to zero
  // don't render as "-$0.00".
  const sign = dollars < 0 && Number(magnitude.toFixed(6)) !== 0 ? "-" : "";
  if (magnitude > 0 && magnitude < 0.01) return `${sign}$${magnitude.toFixed(6)}`;
  return `${sign}$${usdFormatter.format(magnitude)}`;
}

/** Integer cents (e.g. `agent_eval_results.cost_cents`) rendered as dollars. */
export function formatCents(cents: number | null | undefined): string {
  return cents == null ? "—" : formatUsd(cents / 100);
}

/** Token counts, grouped; "—" when we have no count (not zero). */
export function formatTokens(count: number | null | undefined): string {
  if (count == null || !Number.isFinite(count)) return "—";
  return count.toLocaleString();
}

const compactTokenFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** Token counts for dense cells ("4.2M"); pair with `formatTokens` in a tooltip. */
export function formatTokensCompact(count: number | null | undefined): string {
  if (count == null || !Number.isFinite(count)) return "—";
  return compactTokenFormatter.format(count);
}
